import { INVALID_TOKEN, UNAVAILABLE_TOKEN } from 'utils/constants';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { auth } from './firebase';

export const api = axios.create({
	baseURL: process.env.REACT_APP_API_BASE,
	headers: {
		Accept: '*/*',
		'Content-Type': 'application/json',
	},
});

interface CustomToken {
	exp: number;
	iat: number;
	[key: string]: any;
}

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
	_retry?: boolean;
}

const isTokenExpiringSoon = (token: string | null): boolean => {
	if (!token) return true;

	try {
		const decoded = jwtDecode<CustomToken>(token);
		const currentTime = Math.floor(Date.now() / 1000);
		const timeUntilExpiry = decoded.exp - currentTime;

		return timeUntilExpiry < 300;
	} catch {
		return true;
	}
};

const refreshToken = async (): Promise<string | null> => {
	try {
		const currentUser = auth.currentUser;
		if (!currentUser) {
			return null;
		}

		const newToken = await currentUser.getIdToken(true);
		localStorage.setItem('accessToken', newToken);

		const partnerId = localStorage.getItem('currentPartnerId');
		if (partnerId) {
			try {
				const { data } = await api.post('/login/partner', { partner_id: parseInt(partnerId) });
				localStorage.setItem('accessToken', data);
				return data;
			} catch (error) {
				// Failed to re-establish partner context
			}
		}

		return newToken;
	} catch (error) {
		// Token refresh failed
		return null;
	}
};

api.interceptors.request.use(async (request) => {
	if (process.env.REACT_APP_ENV === 'development') {
		if (['login', 'users', 'partners', 'members'].some((url) => request.url?.includes(url))) {
			return { ...request, baseURL: process.env.REACT_APP_API_BASE_USERS };
		}
	}
	return request;
});

api.interceptors.request.use(async (config: AxiosRequestConfig) => {
	let token = localStorage.getItem('accessToken');

	if (!config.url?.includes('/login') && !config.url?.includes('/signup')) {
		if (token && isTokenExpiringSoon(token)) {
			const newToken = await refreshToken();
			if (newToken) {
				token = newToken;
			} else {
				localStorage.clear();
				window.parent.postMessage({ action: 'logout' }, '*');
				window.location.href = '/login';
				return Promise.reject(new Error('Failed to refresh token'));
			}
		}
	}

	const headers = {
		...config.headers,
		Accept: '*/*',
		Authorization: token ? `Bearer ${token}` : '',
	};
	// A FormData body must be sent as multipart/form-data with a boundary, which the
	// browser sets automatically only when Content-Type is left unset. Forcing
	// application/json here strips the boundary and the server cannot parse the upload
	// (e.g. cohort upload silently failing — TIC-333). Only default to JSON for non-FormData bodies.
	if (config.data instanceof FormData) {
		delete (headers as Record<string, unknown>)['Content-Type'];
	} else {
		(headers as Record<string, unknown>)['Content-Type'] = 'application/json';
	}
	return { ...config, headers } as AxiosRequestConfig;
});

api.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as ExtendedAxiosRequestConfig;

		if (
			(error.response?.status === 401 ||
				(error.response?.data as any)?.error?.includes(INVALID_TOKEN) ||
				(error.response?.data as any)?.error === UNAVAILABLE_TOKEN) &&
			originalRequest &&
			!originalRequest._retry &&
			!originalRequest.url?.includes('/login') &&
			!originalRequest.url?.includes('/signup')
		) {
			originalRequest._retry = true;

			const newToken = await refreshToken();
			if (newToken) {
				if (!originalRequest.headers) {
					originalRequest.headers = {};
				}
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				return api(originalRequest);
			} else {
				localStorage.clear();
				window.parent.postMessage({ action: 'logout' }, '*');
				window.location.href = '/login';
			}
		}

		if (error.response?.status === 401 && (originalRequest?.url?.includes('/login') || originalRequest?._retry)) {
			localStorage.clear();
			window.parent.postMessage({ action: 'logout' }, '*');
			window.location.href = '/login';
		}

		return Promise.reject(error);
	}
);

export const getData = async (endpoint: string, options?: AxiosRequestConfig<any>) => {
	const { data } = await api.get(endpoint, options);
	return data;
};

export const getResponse = async (endpoint: string, options = {}) => {
	return await api.get(endpoint, options);
};

export const post = async (endpoint: string, options = {}) => {
	const response = await api.post(endpoint, options);
	return response?.data;
};

export const put = async (endpoint: string, options = {}) => {
	const response = await api.put(endpoint, options);
	return response?.data;
};

export const deleteData = async (endpoint: string, options = {}) => {
	const { data } = await api.delete(endpoint, options);
	return data;
};

export default api;
